// Models
/**
 * Creates a fresh empty identity. Returning distinct instances (rather than sharing one object)
 * avoids accidental shared-reference mutation between the `user`/`application` members of an
 * identity set or across separate {@link createIdentity} fallbacks.
 */ function createEmptyIdentity() {
    return {
        id: "",
        tenantId: ""
    };
}
/** Empty identity used when the service does not provide a user or application identity */ export const EmptyIdentity = createEmptyIdentity();
/** Unknown/empty identity used as a placeholder when actual identity is not available */ export const UnknownIdentity = {
    user: createEmptyIdentity(),
    application: createEmptyIdentity()
};
/**
 * Normalizes a service identity resource into a non-nullable client identity.
 * Falls back to a fresh empty identity when the service omits the identity. Downstream consumers
 * treat an empty `id` as "no/unknown user" (see UnknownUser).
 * @param resource The service identity resource (may be null/undefined)
 */ export function createIdentity(resource) {
    return resource ?? createEmptyIdentity();
}
/**
 * Normalizes a service identity set resource (a discriminated union where `user` or `application`
 * may be absent) into a non-nullable client identity set. Each missing member is replaced with
 * {@link EmptyIdentity} so downstream code never has to null-check `.user`/`.application` before
 * reading their properties.
 * @param resource The service identity set resource (may be null/undefined)
 */ export function createIdentitySet(resource) {
    if (resource == null) {
        return UnknownIdentity;
    }
    return {
        user: createIdentity(resource.user),
        application: createIdentity(resource.application)
    };
}
